import { EntityCard } from "./EntityCard";
import { EntityTypeBadge } from "./entityTypeMeta";
import { Badge } from "../../base/badge";
import { ChevronRight, Activity } from "lucide-react";
import { cn } from "@charphq/ui/lib/utils";
import {
  deploymentStatusBadgeVariant,
  deploymentStatusLabel,
} from "../../../utils/deploymentStatus";

interface Deployment {
  id: string;
  name: string;
  description?: string | null;
  status?: string;
  monitoringEnabled?: boolean;
}

interface DeploymentCardProps {
  deployment: Deployment;
  onClick?: () => void;
  className?: string;
  /** Highlight the card as the active keyboard selection. */
  isSelected?: boolean;
}

export function DeploymentCard({
  deployment,
  onClick,
  className,
  isSelected,
}: DeploymentCardProps) {
  return (
    <EntityCard
      onClick={onClick}
      className={cn(className, isSelected && "border-primary ring-2 ring-primary/50 shadow-md")}
    >
      <div className="flex h-full items-center justify-between">
        {/* Left side - Main content */}
        <div className="flex-1 min-w-0">
          {/* Top row: type badge, name, status */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <EntityTypeBadge kind="deployment" />
              <h3 className="font-medium text-base truncate">{deployment.name}</h3>
            </div>
            {deployment.status && (
              <Badge
                variant={deploymentStatusBadgeVariant(deployment.status)}
                className="text-xs shrink-0 ml-2"
              >
                {deploymentStatusLabel(deployment.status)}
              </Badge>
            )}
          </div>

          {/* Second row: description */}
          {deployment.description && (
            <p className="text-sm text-muted-foreground truncate mb-2">{deployment.description}</p>
          )}

          {/* Third row: monitoring */}
          {deployment.monitoringEnabled && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Activity className="h-3 w-3" />
              <span>Monitoring enabled</span>
            </div>
          )}
        </div>

        {/* Right side - Chevron */}
        <div className="flex flex-col items-end gap-2 shrink-0 ml-4">
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </EntityCard>
  );
}
